import React, { useEffect, useState } from 'react';
import { X, Users, Mail, User, Calendar, ShieldCheck } from 'lucide-react';
import { SkeletonStyles, SkeletonBox } from "../../../components/SkeletonLoader";

function GroupDetailsModal({ group, onClose, theme, fonts, styles, language, text, fetchGroupMembers }) {
    const isRTL = language === 'ar';
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadMembers = async () => {
            const data = await fetchGroupMembers(group.id || group.groupId);
            setMembers(data);
            setLoading(false);
        };
        loadMembers();
    }, [group, fetchGroupMembers]);

    // Handle clicking outside to close
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div style={styles.modal} onClick={handleOverlayClick}>
            <SkeletonStyles theme={theme} />
            <div 
                style={{
                    ...styles.modalContent,
                    direction: isRTL ? 'rtl' : 'ltr',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh',
                    overflow: 'hidden',
                }}
            >
                {/* Header */}
                <div style={styles.modalHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #2e7cba, #10b981)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                        }}>
                            <ShieldCheck size={18} />
                        </div>
                        <h3 style={styles.modalTitle}>
                            {group.groupName}
                        </h3>
                    </div>
                    <button 
                        onClick={onClose} 
                        style={styles.closeButton}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    paddingRight: isRTL ? '0' : '0.5rem',
                    paddingLeft: isRTL ? '0.5rem' : '0',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem', color: theme === 'dark' ? '#cbd5e1' : '#475569' }}>
                        <Users size={18} />
                        <span style={{ fontWeight: '600', fontSize: fonts.body }}>
                            {isRTL ? "الأعضاء المسجلين" : "Enrolled Members"} <span style={{ opacity: 0.7 }}>({members.length})</span>
                        </span>
                    </div>

                    {loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {[1, 2, 3].map(i => (
                                <SkeletonBox key={i} width="100%" height="65px" style={{ borderRadius: "12px" }} />
                            ))}
                        </div>
                    ) : members.length === 0 ? (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '3rem 1rem', 
                            color: theme === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                            background: theme === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                            borderRadius: '12px',
                            border: theme === 'dark' ? '1px dashed rgba(255,255,255,0.1)' : '1px dashed rgba(0,0,0,0.1)'
                        }}>
                            <Users size={32} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
                            <div style={{ fontSize: fonts.body }}>
                                {isRTL ? "لا يوجد أعضاء في هذه المجموعة حتى الآن" : "No members found in this group yet"}
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {members.map(member => (
                                <div key={member.id} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    padding: '0.875rem 1rem',
                                    background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                                    borderRadius: '12px',
                                    border: theme === 'dark' ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.04)',
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f5f9';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = theme === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}>
                                    <div style={{
                                        width: '42px',
                                        height: '42px',
                                        borderRadius: '10px',
                                        background: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'white',
                                        boxShadow: theme === 'light' ? '0 2px 4px rgba(0,0,0,0.02)' : 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: theme === 'dark' ? '#fff' : '#0f172a',
                                        fontWeight: '700',
                                        flexShrink: 0,
                                        fontSize: '15px'
                                    }}>
                                        {member.name ? member.name.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ 
                                            fontWeight: '600', 
                                            fontSize: fonts.body, 
                                            color: theme === 'dark' ? '#f8fafc' : '#0f172a', 
                                            whiteSpace: 'nowrap', 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis' 
                                        }}>
                                            {isRTL ? (member.arabicName || member.name) : (member.name || member.arabicName)}
                                        </div>
                                        {member.email && (
                                            <div style={{ 
                                                fontSize: '0.8125rem', 
                                                color: theme === 'dark' ? '#94a3b8' : '#64748b', 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '0.375rem', 
                                                marginTop: '0.25rem' 
                                            }}>
                                                <Mail size={13} style={{ opacity: 0.8 }} />
                                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.email}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ 
                                        fontSize: '0.75rem', 
                                        color: theme === 'dark' ? '#94a3b8' : '#64748b', 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        alignItems: 'flex-end', 
                                        gap: '0.375rem',
                                        minWidth: '70px'
                                    }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '0.25rem',
                                            background: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                                            padding: '0.125rem 0.5rem',
                                            borderRadius: '1rem',
                                            fontWeight: '500',
                                            color: theme === 'dark' ? '#e2e8f0' : '#334155'
                                        }}>
                                            <User size={10} />
                                            <span>{member.empId}</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', opacity: 0.8 }}>
                                            <Calendar size={11} />
                                            <span>{new Date(member.assignedAt).toLocaleDateString(isRTL ? 'ar-EG' : 'en-US')}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default GroupDetailsModal;
